
import { useState, useEffect } from 'react';

interface HeroSectionProps {
  scrollY: number;
}

export default function HeroSection({ scrollY }: HeroSectionProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          transform: `translateY(${scrollY * 0.5}px)`,
          backgroundImage: `url('/assets/e6b384d387906594421dff4f33317590.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 z-10">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-orange-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-1500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            <span className="bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">
              Your Blueprint
            </span>
            <br />
            <span className="text-white">for Digital Success</span>
          </h1>
          
          <h2 className="text-2xl md:text-3xl font-semibold text-orange-400 mb-8">
            Comprehensive Digital Solutions for a Dynamic World
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            Explore our diverse expertise, meticulously crafted to elevate your brand, 
            automate your operations, and pioneer new digital frontiers.
          </p>
          
          <div className="text-lg text-gray-400 max-w-5xl mx-auto mb-12 leading-relaxed">
            In today's ever-evolving digital landscape, true success requires a blend of strategic vision, 
            creative execution, and cutting-edge technology. At Hash Adz, we offer a holistic suite of 
            solutions designed to meet the unique challenges and opportunities of your business. From 
            crafting compelling brand identities to deploying intelligent automation and exploring the 
            future of Web3, we're here to guide your journey.
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a 
              href="#services-overview"
              className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-3">
                Explore Our Services
                <i className="ri-arrow-down-line group-hover:translate-y-1 transition-transform duration-300"></i>
              </span>
            </a>
            
            <a 
              href="#contact"
              className="group border-2 border-orange-500 hover:bg-orange-500 text-orange-500 hover:text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-3">
                Get Consultation
                <i className="ri-phone-line group-hover:rotate-12 transition-transform duration-300"></i>
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="animate-bounce">
          <i className="ri-arrow-down-line text-orange-400 text-2xl"></i>
        </div>
      </div>
    </section>
  );
}
